import React, { useState, useEffect, useMemo } from 'react';

import {
	getCloudIdFromResource,
	getProductIdFromResource,
	getProductTitleFromAri,
	normalizeJiraSubProduct,
} from '../../utils';
import {
	type FeaturedUserProvider,
	type InviteeProvider,
	type SubProduct,
	type UserRole,
	type ViewAs,
} from '../../types';
import { defaultAccountInfoClient, defaultPermsApiClient } from '../../clients';
import { invitePeopleRendered, invitePeopleRenderedTimings } from '../../analytics/ufoExperiences';
import { VIRALITY_GROWTH_INITIATIVE } from '../../experiments/constants';
import { useAnalyticsWithAttributes, useCommonAttributesFrom } from '../../analytics';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import { type Plugins } from '../../plugins/types';
import {
	InvitePeopleStateContext,
	type InvitePeopleState,
	useInvitePeopleContext,
} from './InvitePeopleContextInternal';
import { useExperimentalFeatures } from '../../experiments/experimentalFeatures';

const DEFAULT_SOURCE = 'unknown';
const DEFAULT_VIEW_AS = 'unknown';

export interface InvitePeopleContextProps {
	children: React.ReactNode;
	inviteeProvider?: InviteeProvider;
	plugins?: Plugins;
	resourceAri: string;
	source?: string;
	subProduct?: SubProduct;
	userEmail?: string;
	accountId?: string;
	userRole?: UserRole;
	viewAs?: ViewAs;
	orgId?: string;
	disableNoInviteError?: boolean;
	experimental_featuredUsers?: FeaturedUserProvider;
	experimental_inlineThirdPartyButtons?: boolean;
	experimental_productContext?: Record<string, string>;
}

export const FLOW_CORE_INVITES = 'core-invites';

export { useInvitePeopleContext };

export const InvitePeopleContext = ({
	children,
	inviteeProvider,
	plugins,
	resourceAri,
	source = DEFAULT_SOURCE,
	subProduct,
	userEmail: userEmailProp,
	accountId: accountIdProp,
	userRole: userRoleProp,
	viewAs = DEFAULT_VIEW_AS,
	orgId,
	disableNoInviteError = false,
	experimental_featuredUsers,
	experimental_inlineThirdPartyButtons,
	experimental_productContext,
}: InvitePeopleContextProps) => {
	const [userEmail, setUserEmail] = useState(userEmailProp);
	const [accountId, setAccountId] = useState<string | undefined>(accountIdProp);

	const needToFetchUserEmail = !userEmailProp || !accountIdProp;
	const [isUserEmailLoading, setIsUserEmailLoading] = useState(needToFetchUserEmail);

	const [userRole, setUserRole] = useState(userRoleProp || 'basic'); // TODO shouldn't this default to undefined while we load it?
	const needToFetchUserRole = !userRoleProp;
	const [isUserRoleLoading, setIsUserRoleLoading] = useState(needToFetchUserRole);

	const isLoading = isUserEmailLoading || isUserRoleLoading;

	const {
		inviteFromAssignModal: inviteFromAssignModalCohort,
		isEligibleForInviteAndAssignFromBoard,
		invitePromptOnLinkV3: invitePromptOnLinkV3Cohort,
	} = useExperimentalFeatures();

	const contextValue = useMemo<InvitePeopleState>(() => {
		const productId = getProductIdFromResource(resourceAri) || '';
		const [, loggedInEmailDomain] = userEmail?.split('@') ?? [];

		return {
			flow: FLOW_CORE_INVITES,
			growthInitiative: VIRALITY_GROWTH_INITIATIVE,
			cloudId: getCloudIdFromResource(resourceAri),
			inviteeProvider,
			plugins,
			productId,
			productTitle: getProductTitleFromAri(resourceAri),
			jiraSubProduct:
				productId === 'jira' && subProduct && subProduct !== 'unknown'
					? normalizeJiraSubProduct(subProduct)
					: subProduct,
			source,
			subProduct,
			userRole,
			viewAs,
			inviteFromAssignModalCohort,
			invitePromptOnLinkV3Cohort,
			isEligibleForInviteAndAssignFromBoard,
			orgId,
			isLoading,
			loggedInEmailDomain,
			userEmail,
			accountId,
			disableNoInviteError,
			experimental_featuredUsers,
			experimental_inlineThirdPartyButtons,
			experimental_productContext,
		};
	}, [
		disableNoInviteError,
		isEligibleForInviteAndAssignFromBoard,
		inviteFromAssignModalCohort,
		invitePromptOnLinkV3Cohort,
		inviteeProvider,
		isLoading,
		orgId,
		plugins,
		resourceAri,
		source,
		subProduct,
		userEmail,
		accountId,
		userRole,
		viewAs,
		experimental_featuredUsers,
		experimental_inlineThirdPartyButtons,
		experimental_productContext,
	]);

	const analytics = useAnalyticsWithAttributes(
		InvitePeopleAnalytics,
		useCommonAttributesFrom(contextValue),
	);

	useEffect(() => {
		const fetchUserEmail = async () => {
			try {
				invitePeopleRendered.mark(invitePeopleRenderedTimings.getAccountInfo.startMark);
				const accountInfo = await defaultAccountInfoClient.getAccountInfo();
				invitePeopleRendered.mark(invitePeopleRenderedTimings.getAccountInfo.endMark);
				setUserEmail(accountInfo.email);
				setAccountId(accountInfo.account_id);
			} catch (err) {
				analytics.accountInfoFailed(String(err));
			} finally {
				setIsUserEmailLoading(false);
			}
		};

		if (needToFetchUserEmail) {
			fetchUserEmail();
		}
	}, [analytics, needToFetchUserEmail]);

	const cloudId = contextValue.cloudId;

	useEffect(() => {
		async function fetchUserRole() {
			try {
				setIsUserRoleLoading(true);
				invitePeopleRendered.mark(invitePeopleRenderedTimings.getUserPermission.startMark);
				const perms = await defaultPermsApiClient.getUserRole(cloudId as string);
				invitePeopleRendered.mark(invitePeopleRenderedTimings.getUserPermission.endMark);
				if (perms.errorMessage) {
					analytics.getPermsFailed(`ari:cloud:platform::site/${cloudId}`, perms.errorMessage);
				} else {
					analytics.getPermsSucceeded(
						`ari:cloud:platform::site/${cloudId}`,
						perms.isInviteUsersPermitted,
						perms.isManagePermitted,
					);
				}
				setUserRole(perms.role);
			} finally {
				setIsUserRoleLoading(false);
			}
		}

		if (needToFetchUserRole) {
			fetchUserRole();
		}
	}, [cloudId, resourceAri, analytics, userRoleProp, needToFetchUserRole]);

	return (
		<InvitePeopleStateContext.Provider value={contextValue}>
			{children}
		</InvitePeopleStateContext.Provider>
	);
};
